import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { calculateOurOfferMetrics } from "@/utils/calculations";

export const RevenueAnalysis = ({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts }) => {

  const calculations = useMemo(() => {
    const metrics = calculateOurOfferMetrics(formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts);
    if (!metrics) return null;

    const conversionFactor = 0.092903;
    const totalFSIArea = fsiBreakdown.totalFSIWithAncillary || 0;
    const additionalFreeCarpet = metrics.additionalFreeCarpetFlats + metrics.additionalFreeCarpetOffices + metrics.additionalFreeCarpetShops;
    const finalSaleArea = metrics.finalSaleAreaSqft * conversionFactor;
    
    const freeAreaToSocietySqft = parseFloat(formData.freeAreaToSociety) || 0;
    const freeAreaToSocietySqMtr = freeAreaToSocietySqft * conversionFactor;
    const netCarpetForSale = Math.max(0, metrics.balanceCarpetArea - freeAreaToSocietySqMtr);

    return {
      totalFSIArea,
      totalCarpetArea: metrics.totalCarpetArea,
      existingCarpetArea: metrics.existingCarpetArea,
      additionalFreeCarpet,
      additionalFreeCarpetFlats: metrics.additionalFreeCarpetFlats,
      additionalFreeCarpetOffices: metrics.additionalFreeCarpetOffices,
      additionalFreeCarpetShops: metrics.additionalFreeCarpetShops,
      freeAreaToSocietySqMtr,
      freeAreaToSocietySqft,
      totalTerraceGardenWithIncrement: metrics.totalTerraceGardenWithIncrement,
      parkingCompensationCarpet: metrics.parkingCompensationCarpet,
      balanceCarpetArea: metrics.balanceCarpetArea,
      netCarpetForSale,
      finalSaleArea,
      finalSaleAreaSqft: metrics.finalSaleAreaSqft,
      // Revenue breakdown by property type
      saleableAreaFlats: metrics.saleableAreaFlats,
      saleableAreaOffices: metrics.saleableAreaOffices,
      saleableAreaShops: metrics.saleableAreaShops,
      revenueFlats: metrics.revenueFlats,
      revenueOffices: metrics.revenueOffices,
      revenueShops: metrics.revenueShops,
      saleRateFlats: metrics.saleRateFlats,
      saleRateOffices: metrics.saleRateOffices,
      saleRateShops: metrics.saleRateShops,
      totalSaleRevenue: metrics.totalSaleRevenue,
      totalRevenue: metrics.totalRevenue,
      // JV data
      isGreenfield: metrics.isGreenfield,
      jvType: metrics.jvType,
      jvRatioFlats: metrics.jvRatioFlats,
      jvRatioOffices: metrics.jvRatioOffices,
      jvRatioShops: metrics.jvRatioShops,
      jvPartnerAreaSqft: metrics.jvPartnerAreaSqft,
      jvPartnerRevenue: metrics.jvPartnerRevenue,
      developerSaleAreaSqft: metrics.developerSaleAreaSqft,
    };
  }, [fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, formData]);

  // Helper to format currency in Cr
  const formatCrores = (value) => {
    if (!value || value === 0) return "₹0";
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  };

  // Helper to format number with commas
  const formatNumber = (value, decimals = 0) => {
    if (!value || isNaN(value)) return "0";
    return value.toLocaleString('en-IN', { maximumFractionDigits: decimals });
  };

  const isGreenfield = formData.projectMode === "greenfield";

  return (
    <div className="space-y-6">
      {isGreenfield ? (
        /* ============ GREENFIELD JV REVENUE LAYOUT ============ */
        <>
          <Card className="rounded-sm shadow-sm border-blue-500/50">
            <CardHeader className="bg-blue-50 border-b">
              <CardTitle className="font-heading tracking-tight text-blue-800">Area & Revenue Summary</CardTitle>
              <CardDescription>Total buildable area, JV partner allocation, and developer's saleable area</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Step 1: Total Area from FSI */}
              <div className="space-y-2 text-sm">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">1. Total Area from FSI</h4>
                <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                  <span className="text-muted-foreground">Total Built-up Area (FSI with Ancillary)</span>
                  <span className="font-mono font-semibold">{calculations?.totalFSIArea?.toFixed(2) || '0.00'} sq mtr</span>
                </div>
                <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
                  <span className="text-muted-foreground">Total Carpet Area (Built-up × {formData.carpetAreaFactor || 70}%)</span>
                  <span className="font-mono font-semibold">{calculations?.totalCarpetArea?.toFixed(2) || '0.00'} sq mtr</span>
                </div>
                <div className="flex justify-between p-3 bg-blue-100 rounded-sm font-semibold">
                  <span className="text-blue-800">Total Saleable Area (Carpet + {formData.loadingPercentage || 40}% loading)</span>
                  <span className="font-mono text-blue-800">{calculations?.finalSaleArea?.toFixed(2) || '0.00'} sq mtr ({formatNumber(calculations?.finalSaleAreaSqft, 0)} sft)</span>
                </div>
              </div>

              <Separator />

              {/* Step 2: JV Partner's Share */}
              <div className="space-y-2 text-sm">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">2. Landowner's Share (JV)</h4>
                {calculations?.saleableAreaFlats > 0 && (
                  <div className="flex justify-between p-2 bg-amber-50 rounded-sm">
                    <span className="text-amber-700">Flats ({calculations.jvRatioFlats}%)</span>
                    <span className="font-mono text-amber-700">{formatNumber(calculations.saleableAreaFlats * (calculations.jvRatioFlats / 100), 0)} sft</span>
                  </div>
                )}
                {calculations?.saleableAreaOffices > 0 && (
                  <div className="flex justify-between p-2 bg-amber-50 rounded-sm">
                    <span className="text-amber-700">Offices ({calculations.jvRatioOffices}%)</span>
                    <span className="font-mono text-amber-700">{formatNumber(calculations.saleableAreaOffices * (calculations.jvRatioOffices / 100), 0)} sft</span>
                  </div>
                )}
                {calculations?.saleableAreaShops > 0 && (
                  <div className="flex justify-between p-2 bg-amber-50 rounded-sm">
                    <span className="text-amber-700">Shops ({calculations.jvRatioShops}%)</span>
                    <span className="font-mono text-amber-700">{formatNumber(calculations.saleableAreaShops * (calculations.jvRatioShops / 100), 0)} sft</span>
                  </div>
                )}
                <div className="flex justify-between p-3 bg-amber-100 rounded-sm font-semibold">
                  <span className="text-amber-800">Total Landowner Area</span>
                  <span className="font-mono text-amber-800">
                    {formatNumber(
                      (calculations?.saleableAreaFlats || 0) * ((calculations?.jvRatioFlats || 0) / 100) +
                      (calculations?.saleableAreaOffices || 0) * ((calculations?.jvRatioOffices || 0) / 100) +
                      (calculations?.saleableAreaShops || 0) * ((calculations?.jvRatioShops || 0) / 100)
                    , 0)} sft
                  </span>
                </div>
              </div>

              <Separator />

              {/* Step 3: Developer's Area for Sale */}
              <div className="space-y-2 text-sm">
                <h4 className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">3. Developer's Area for Sale</h4>
                {calculations?.saleableAreaFlats > 0 && (
                  <div className="flex justify-between p-2 bg-green-50 rounded-sm">
                    <span className="text-green-700">Flats ({100 - (calculations.jvRatioFlats || 0)}%)</span>
                    <span className="font-mono text-green-700">{formatNumber(calculations.saleableAreaFlats * (1 - calculations.jvRatioFlats / 100), 0)} sft @ ₹{formatNumber(calculations.saleRateFlats, 0)}/sft = {formatCrores(calculations.saleableAreaFlats * (1 - calculations.jvRatioFlats / 100) * calculations.saleRateFlats)}</span>
                  </div>
                )}
                {calculations?.saleableAreaOffices > 0 && (
                  <div className="flex justify-between p-2 bg-green-50 rounded-sm">
                    <span className="text-green-700">Offices ({100 - (calculations.jvRatioOffices || 0)}%)</span>
                    <span className="font-mono text-green-700">{formatNumber(calculations.saleableAreaOffices * (1 - calculations.jvRatioOffices / 100), 0)} sft @ ₹{formatNumber(calculations.saleRateOffices, 0)}/sft = {formatCrores(calculations.saleableAreaOffices * (1 - calculations.jvRatioOffices / 100) * calculations.saleRateOffices)}</span>
                  </div>
                )}
                {calculations?.saleableAreaShops > 0 && (
                  <div className="flex justify-between p-2 bg-green-50 rounded-sm">
                    <span className="text-green-700">Shops ({100 - (calculations.jvRatioShops || 0)}%)</span>
                    <span className="font-mono text-green-700">{formatNumber(calculations.saleableAreaShops * (1 - calculations.jvRatioShops / 100), 0)} sft @ ₹{formatNumber(calculations.saleRateShops, 0)}/sft = {formatCrores(calculations.saleableAreaShops * (1 - calculations.jvRatioShops / 100) * calculations.saleRateShops)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between p-3 bg-green-100 rounded-sm font-semibold">
                  <span className="text-green-800">Developer Sale Area</span>
                  <span className="font-mono text-green-800">{formatNumber(calculations?.developerSaleAreaSqft, 0)} sft</span>
                </div>
                <div className="flex justify-between p-3 bg-chart-1/20 rounded-sm font-semibold">
                  <span className="text-chart-1">Gross Revenue</span>
                  <span className="font-mono text-chart-1">{formatCrores(calculations?.totalSaleRevenue)}</span>
                </div>
                {calculations?.totalSaleRevenue !== calculations?.totalRevenue && (
                  <>
                    <div className="flex justify-between p-2 bg-amber-50 rounded-sm text-sm">
                      <span className="text-amber-700">Less: Landowner Share</span>
                      <span className="font-mono text-amber-700">-{formatCrores(calculations.totalSaleRevenue - calculations.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-chart-1/30 rounded-sm font-semibold">
                      <span className="text-chart-1">Developer Revenue (Net)</span>
                      <span className="font-mono text-chart-1">{formatCrores(calculations?.totalRevenue)}</span>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        /* ============ REDEVELOPMENT REVENUE LAYOUT (existing) ============ */
        <>
        <Card className="rounded-sm shadow-sm border-green-500/50">
        <CardHeader className="bg-green-50 border-b">
          <CardTitle className="font-heading tracking-tight text-green-800">Carpet Area to be Given to Society</CardTitle>
          <CardDescription>Total carpet area breakdown showing all free areas</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {/* Components of Free Area to Society */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Total Existing Carpet Area (Flats + Offices + Shops)</span>
              <span className="font-mono font-semibold">{calculations?.existingCarpetArea?.toFixed(2) || '0.00'} sq mtr</span>
            </div>
            {(calculations?.additionalFreeCarpetFlats || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Free Area - Flats ({formData.freeAreaPercentFlats || 0}%)</span>
                <span className="font-mono">{calculations.additionalFreeCarpetFlats.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.additionalFreeCarpetOffices || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Free Area - Offices ({formData.freeAreaPercentOffices || 0}%)</span>
                <span className="font-mono">{calculations.additionalFreeCarpetOffices.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.additionalFreeCarpetShops || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Free Area - Shops ({formData.freeAreaPercentShops || 0}%)</span>
                <span className="font-mono">{calculations.additionalFreeCarpetShops.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.totalTerraceGardenWithIncrement || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Terrace/Garden (converted + increment)</span>
                <span className="font-mono">{calculations.totalTerraceGardenWithIncrement.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.parkingCompensationCarpet || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Parking Compensation (Carpet)</span>
                <span className="font-mono">{calculations.parkingCompensationCarpet.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.freeAreaToSocietySqMtr || 0) > 0 && (
              <div className="flex justify-between p-2">
                <span className="text-muted-foreground">(+) Additional Free Area Given to Society</span>
                <span className="font-mono">{calculations.freeAreaToSocietySqMtr.toFixed(2)} sq mtr</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between p-3 bg-green-100 rounded-sm font-semibold">
              <span className="text-green-800">= Total Carpet Area to be Given</span>
              <span className="font-mono text-green-800">
                {((calculations?.existingCarpetArea || 0) + 
                  (calculations?.additionalFreeCarpet || 0) + 
                  (calculations?.totalTerraceGardenWithIncrement || 0) +
                  (calculations?.parkingCompensationCarpet || 0) +
                  (calculations?.freeAreaToSocietySqMtr || 0)).toFixed(2)} sq mtr
              </span>
            </div>
          </div>
          
          {/* Balance Carpet for Sale - step-by-step matching calculations.js */}
          <div className="space-y-2 text-sm border-t pt-4">
            <div className="flex justify-between p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Total Carpet Area (FSI × {formData.carpetAreaFactor || 70}%)</span>
              <span className="font-mono font-semibold">{calculations?.totalCarpetArea?.toFixed(2) || '0.00'} sq mtr</span>
            </div>
            <div className="flex justify-between p-2 text-destructive">
              <span>(-) Existing Carpet Area</span>
              <span className="font-mono">{calculations?.existingCarpetArea?.toFixed(2) || '0.00'} sq mtr</span>
            </div>
            {(calculations?.additionalFreeCarpet || 0) > 0 && (
              <div className="flex justify-between p-2 text-destructive">
                <span>(-) Additional Free Area ({formData.freeAreaPercentFlats || 0}%)</span>
                <span className="font-mono">{calculations.additionalFreeCarpet.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.totalTerraceGardenWithIncrement || 0) > 0 && (
              <div className="flex justify-between p-2 text-destructive">
                <span>(-) Terrace/Garden (with increment)</span>
                <span className="font-mono">{calculations.totalTerraceGardenWithIncrement.toFixed(2)} sq mtr</span>
              </div>
            )}
            {(calculations?.parkingCompensationCarpet || 0) > 0 && (
              <div className="flex justify-between p-2 text-destructive">
                <span>(-) Parking Compensation (Carpet)</span>
                <span className="font-mono">{calculations.parkingCompensationCarpet.toFixed(2)} sq mtr</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between p-3 bg-blue-50 rounded-sm font-semibold">
              <span className="text-blue-800">= Balance Carpet Area</span>
              <span className="font-mono text-blue-800">{calculations?.balanceCarpetArea?.toFixed(2) || '0.00'} sq mtr</span>
            </div>
            {(calculations?.freeAreaToSocietySqMtr || 0) > 0 && (
              <div className="flex justify-between p-2 text-destructive">
                <span>(-) Free Area Given to Society</span>
                <span className="font-mono">{calculations.freeAreaToSocietySqMtr.toFixed(2)} sq mtr</span>
              </div>
            )}
            <div className="flex justify-between p-3 bg-blue-100 rounded-sm font-semibold">
              <span className="text-blue-800">= Net Carpet Area for Sale</span>
              <span className="font-mono text-blue-800">{calculations?.netCarpetForSale?.toFixed(2) || '0.00'} sq mtr</span>
            </div>
            <div className="flex justify-between p-3 bg-accent/20 rounded-sm font-semibold">
              <span className="text-accent">= Saleable Area (+ {formData.loadingPercentage || 40}% loading)</span>
              <span className="font-mono text-accent">{calculations?.finalSaleArea?.toFixed(2) || '0.00'} sq mtr ({calculations?.finalSaleAreaSqft?.toFixed(0) || '0'} sft)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Saleable Area & Revenue Breakdown - Only for Commercial/Mixed */}
      {(formData.projectType === "commercial" || formData.projectType === "mixed") && 
       (calculations?.saleableAreaFlats > 0 || calculations?.saleableAreaOffices > 0 || calculations?.saleableAreaShops > 0) && (
        <Card className="rounded-sm shadow-sm border-chart-1/50">
          <CardHeader className="bg-chart-1/10 border-b">
            <CardTitle className="font-heading tracking-tight text-chart-1">Saleable Area & Revenue Breakdown</CardTitle>
            <CardDescription>Distribution of saleable area and projected revenue by property type</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Saleable Area Split */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">Saleable Area Split</h4>
                {calculations?.saleableAreaFlats > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <span className="text-muted-foreground">Flats</span>
                    <span className="font-mono font-semibold">{formatNumber(calculations.saleableAreaFlats, 0)} sft</span>
                  </div>
                )}
                {calculations?.saleableAreaOffices > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <span className="text-muted-foreground">Offices</span>
                    <span className="font-mono font-semibold">{formatNumber(calculations.saleableAreaOffices, 0)} sft</span>
                  </div>
                )}
                {calculations?.saleableAreaShops > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <span className="text-muted-foreground">Shops</span>
                    <span className="font-mono font-semibold">{formatNumber(calculations.saleableAreaShops, 0)} sft</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between p-2 bg-accent/20 rounded-sm font-semibold">
                  <span>Total Saleable Area</span>
                  <span className="font-mono">{formatNumber(calculations?.finalSaleAreaSqft, 0)} sft</span>
                </div>
              </div>

              {/* Revenue Split */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm">Revenue Projection</h4>
                {calculations?.revenueFlats > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <div>
                      <span className="text-muted-foreground">Flats</span>
                      <p className="text-xs text-muted-foreground">@ ₹{formatNumber(calculations.saleRateFlats, 0)}/sft</p>
                    </div>
                    <span className="font-mono font-semibold">{formatCrores(calculations.revenueFlats)}</span>
                  </div>
                )}
                {calculations?.revenueOffices > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <div>
                      <span className="text-muted-foreground">Offices</span>
                      <p className="text-xs text-muted-foreground">@ ₹{formatNumber(calculations.saleRateOffices, 0)}/sft</p>
                    </div>
                    <span className="font-mono font-semibold">{formatCrores(calculations.revenueOffices)}</span>
                  </div>
                )}
                {calculations?.revenueShops > 0 && (
                  <div className="flex justify-between p-2 bg-secondary/30 rounded-sm">
                    <div>
                      <span className="text-muted-foreground">Shops</span>
                      <p className="text-xs text-muted-foreground">@ ₹{formatNumber(calculations.saleRateShops, 0)}/sft</p>
                    </div>
                    <span className="font-mono font-semibold">{formatCrores(calculations.revenueShops)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between p-2 bg-chart-1/20 rounded-sm font-semibold">
                  <span className="text-chart-1">Total Revenue</span>
                  <span className="font-mono text-chart-1">{formatCrores(calculations?.totalSaleRevenue)}</span>
                </div>
                {calculations?.isGreenfield && calculations?.totalSaleRevenue !== calculations?.totalRevenue && (
                  <>
                    <div className="flex justify-between p-2 bg-amber-50 rounded-sm text-sm">
                      <span className="text-amber-700">Less: Landowner Share (JV)</span>
                      <span className="font-mono text-amber-700">-{formatCrores(calculations.totalSaleRevenue - calculations.totalRevenue)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-chart-1/30 rounded-sm font-semibold">
                      <span className="text-chart-1">Developer Revenue (Net)</span>
                      <span className="font-mono text-chart-1">{formatCrores(calculations?.totalRevenue)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Area Details (in sq mtr) - Collapsed/Summary view */}
      <Card className="rounded-sm shadow-sm border-accent/50">
        <CardHeader>
          <CardTitle className="font-heading tracking-tight">Existing Area Details (sq mtr)</CardTitle>
          <CardDescription>Individual area components (converted from sft)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            {formData.hasFlats && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Flats Area</p>
                <p className="font-mono font-semibold" data-testid="existing-flats-sqm">
                  {((parseFloat(formData.areaFlats) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
              </div>
            )}
            {formData.hasOffices && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Offices Area</p>
                <p className="font-mono font-semibold" data-testid="existing-offices-sqm">
                  {((parseFloat(formData.areaOffices) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
              </div>
            )}
            {formData.hasShops && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Shops Area</p>
                <p className="font-mono font-semibold" data-testid="existing-shops-sqm">
                  {((parseFloat(formData.areaShops) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
              </div>
            )}
            {(parseFloat(formData.areaTerrace) > 0) && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Terrace Area (Adjacent)</p>
                <p className="font-mono font-semibold" data-testid="existing-terrace-sqm">
                  {((parseFloat(formData.areaTerrace) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
                <p className="text-xs text-muted-foreground">@ {formData.terraceConversionRatio || 25}% = {(((parseFloat(formData.areaTerrace) || 0) * 0.092903) * (parseFloat(formData.terraceConversionRatio) || 25) / 100).toFixed(2)} sq mtr</p>
              </div>
            )}
            {(parseFloat(formData.areaOverheadTerrace) > 0) && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Terrace Area (Overhead)</p>
                <p className="font-mono font-semibold" data-testid="existing-overhead-terrace-sqm">
                  {((parseFloat(formData.areaOverheadTerrace) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
                <p className="text-xs text-muted-foreground">@ {formData.overheadTerraceConversionRatio || 25}% = {(((parseFloat(formData.areaOverheadTerrace) || 0) * 0.092903) * (parseFloat(formData.overheadTerraceConversionRatio) || 25) / 100).toFixed(2)} sq mtr</p>
              </div>
            )}
            {(parseFloat(formData.areaGarden) > 0) && (
              <div className="bg-secondary/50 p-3 rounded-sm">
                <p className="text-xs text-muted-foreground mb-1">Garden Area</p>
                <p className="font-mono font-semibold" data-testid="existing-garden-sqm">
                  {((parseFloat(formData.areaGarden) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
                <p className="text-xs text-muted-foreground">@ {formData.gardenConversionRatio || 25}% = {(((parseFloat(formData.areaGarden) || 0) * 0.092903) * (parseFloat(formData.gardenConversionRatio) || 25) / 100).toFixed(2)} sq mtr</p>
              </div>
            )}
            {(parseFloat(formData.freeAreaToSociety) > 0) && (
              <div className="bg-green-50 p-3 rounded-sm border border-green-200">
                <p className="text-xs text-green-700 mb-1">Free Area to Society</p>
                <p className="font-mono font-semibold text-green-800" data-testid="free-area-society-sqm">
                  {((parseFloat(formData.freeAreaToSociety) || 0) * 0.092903).toFixed(2)} sq mtr
                </p>
                <p className="text-xs text-muted-foreground">({parseFloat(formData.freeAreaToSociety).toLocaleString('en-IN')} sft)</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Carpet Area Calculation - Now Read-Only */}
      <Card className="rounded-sm shadow-sm bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading tracking-tight text-sm">Key Factors (Read-Only)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Carpet Factor: </span>
              <span className="font-mono font-semibold">{formData.carpetAreaFactor || 70}%</span>
            </div>
            <div className="p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Loading - Flats: </span>
              <span className="font-mono font-semibold">{formData.loadingPercentage || formData.loadingPercentageFlats || 40}%</span>
            </div>
            {formData.hasOffices && (
              <div className="p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Loading - Offices: </span>
                <span className="font-mono font-semibold">{formData.loadingPercentageOffices || 35}%</span>
              </div>
            )}
            {formData.hasShops && (
              <div className="p-2 bg-secondary/50 rounded-sm">
                <span className="text-muted-foreground">Loading - Shops: </span>
                <span className="font-mono font-semibold">{formData.loadingPercentageShops || 35}%</span>
              </div>
            )}
            <div className="p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Sale Rate - Flats: </span>
              <span className="font-mono font-semibold">₹{parseInt(formData.saleRateFlats || 0).toLocaleString('en-IN')}/sft</span>
            </div>
            <div className="p-2 bg-secondary/50 rounded-sm">
              <span className="text-muted-foreground">Construction: </span>
              <span className="font-mono font-semibold">₹{parseInt(formData.constructionCostPerSqft || 0).toLocaleString('en-IN')}/sft</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {!fsiBreakdown && (
        <Card className="rounded-sm shadow-sm bg-secondary/50">
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>Please complete FSI Calculator first to view revenue analysis</p>
          </CardContent>
        </Card>
      )}
      {/* Close redevelopment branch */}
      </>
      )}
    </div>
  );
};

export default RevenueAnalysis;
